/**
 * Link Safety Utilities
 * Provides validation for URLs to prevent XSS and other injection attacks
 *
 * Security Features:
 * - Blocks dangerous protocols (javascript:, data:, vbscript:, file:)
 * - Validates document link format
 * - Sanitizes external URLs
 * - Classifies link types for proper handling
 *
 * OWASP: A03:2021 - Injection
 * Mitigates: XSS via javascript: URLs, data: URLs, and malicious protocols
 */

import { log } from '../errors/logger';

/**
 * Dangerous protocols that can execute code or expose sensitive data
 * These protocols are blocked to prevent XSS attacks
 */
const DANGEROUS_PROTOCOLS = [
  'javascript:',  // XSS: javascript:alert(document.cookie)
  'data:',        // XSS: data:text/html,<script>alert(1)</script>
  'vbscript:',    // XSS: vbscript:msgbox(1) (older IE)
  'file:',        // File system access
  'blob:',        // Potential abuse with crafted blobs
];

/**
 * Safe protocols allowed in links
 * Only HTTP(S) and mailto are considered safe for external links
 */
const SAFE_PROTOCOLS = [
  'http:',
  'https:',
  'mailto:',
];

/**
 * Validate if a URL is safe to use in links
 *
 * Security checks:
 * 1. Block empty/null URLs
 * 2. Block dangerous protocols (javascript:, data:, etc.)
 * 3. Allow safe protocols (http:, https:, mailto:)
 * 4. Allow relative URLs (starting with /)
 * 5. Allow anchor links (starting with #)
 *
 * @param url - URL to validate
 * @returns true if URL is safe, false if dangerous
 *
 * @example
 * isSafeUrl('https://example.com')       // true
 * isSafeUrl('javascript:alert(1)')       // false
 * isSafeUrl('data:text/html,<script>')   // false
 * isSafeUrl('/internal/page')            // true
 * isSafeUrl('#section')                  // true
 */
export const isSafeUrl = (url: string | undefined): boolean => {
  if (!url) {
    return false;
  }

  const trimmedUrl = url.trim();
  if (!trimmedUrl) {
    return false;
  }

  const lowerUrl = trimmedUrl.toLowerCase();

  // Block dangerous protocols
  for (const proto of DANGEROUS_PROTOCOLS) {
    if (lowerUrl.startsWith(proto)) {
      log.warn('[Link Safety] Blocked dangerous URL protocol', {
        url: trimmedUrl.substring(0, 50), // Log first 50 chars only
        protocol: proto
      });
      return false;
    }
  }

  // Allow safe protocols
  for (const proto of SAFE_PROTOCOLS) {
    if (lowerUrl.startsWith(proto)) {
      return true;
    }
  }

  // Allow relative URLs (must start with / but not //)
  if (trimmedUrl.startsWith('/') && !trimmedUrl.startsWith('//')) {
    return true;
  }

  // Allow anchor links
  if (trimmedUrl.startsWith('#')) {
    return true;
  }

  // Allow document: protocol (internal app protocol)
  if (trimmedUrl.startsWith('document:')) {
    return true;
  }

  // Reject unknown protocols or suspicious patterns
  log.warn('[Link Safety] Blocked URL with unknown protocol', {
    url: trimmedUrl.substring(0, 50)
  });
  return false;
};

/**
 * Check if URL is an external link (http/https)
 *
 * @param url - URL to check
 * @returns true if external link, false otherwise
 *
 * @example
 * isExternalUrl('https://example.com')  // true
 * isExternalUrl('http://example.com')   // true
 * isExternalUrl('/internal/page')       // false
 * isExternalUrl('document:123')         // false
 */
export const isExternalUrl = (url: string): boolean => {
  const lowerUrl = url.toLowerCase().trim();
  return lowerUrl.startsWith('http://') || lowerUrl.startsWith('https://');
};

/**
 * Check if URL is a document link (internal app protocol)
 *
 * @param url - URL to check
 * @returns true if document link, false otherwise
 *
 * @example
 * isDocumentUrl('document:12345')  // true
 * isDocumentUrl('document:abc-def') // true
 * isDocumentUrl('https://example.com') // false
 */
export const isDocumentUrl = (url: string): boolean => {
  return url.trim().toLowerCase().startsWith('document:');
};

/**
 * Validate document ID format
 * Only allows alphanumeric characters, hyphens, and underscores
 * Prevents injection attacks via malformed document IDs
 *
 * @param documentId - Document ID to validate
 * @returns true if valid format, false otherwise
 *
 * @example
 * isValidDocumentId('12345')        // true
 * isValidDocumentId('abc-def-123')  // true
 * isValidDocumentId('doc_123')      // true
 * isValidDocumentId('../../../etc') // false
 * isValidDocumentId('<script>')     // false
 */
export const isValidDocumentId = (documentId: string): boolean => {
  if (!documentId) {
    return false;
  }

  // Only allow alphanumeric, hyphen, underscore
  // This prevents path traversal and injection attacks
  const validPattern = /^[a-zA-Z0-9_-]+$/;

  if (!validPattern.test(documentId)) {
    log.warn('[Link Safety] Invalid document ID format', {
      documentId: documentId.substring(0, 50)
    });
    return false;
  }

  // Additional length check (reasonable document ID length)
  if (documentId.length > 100) {
    log.warn('[Link Safety] Document ID too long', {
      length: documentId.length
    });
    return false;
  }

  return true;
};

/**
 * Extract and validate document ID from document: URL
 *
 * @param url - Document URL (format: document:documentId)
 * @returns document ID if valid, null if invalid
 *
 * @example
 * extractDocumentId('document:12345')    // '12345'
 * extractDocumentId('document:abc-def')  // 'abc-def'
 * extractDocumentId('document:<script>') // null (invalid)
 */
export const extractDocumentId = (url: string): string | null => {
  if (!isDocumentUrl(url)) {
    return null;
  }

  // Extract ID after 'document:' prefix
  const documentId = url.substring(9); // 'document:'.length === 9

  if (!isValidDocumentId(documentId)) {
    return null;
  }

  return documentId;
};

/**
 * Sanitize URL for display (prevent visual spoofing)
 * Removes control characters and normalizes whitespace
 *
 * @param url - URL to sanitize
 * @returns sanitized URL
 *
 * @example
 * sanitizeUrlForDisplay('https://example.com\n\r')  // 'https://example.com'
 */
export const sanitizeUrlForDisplay = (url: string): string => {
  if (!url) {
    return '';
  }

  // Remove control characters (including null bytes, newlines, etc.)
  // eslint-disable-next-line no-control-regex
  return url.replace(/[\x00-\x1F\x7F]/g, '').trim();
};
